import { menuItems } from '@/layout/const'

export type SidebarMenuItem = (typeof menuItems)[number]
export type SidebarSubMenuItem = NonNullable<SidebarMenuItem['subItems']>[number]

export type SidebarSubMenuItemWithMatch = SidebarSubMenuItem & {
    exactMatch?: boolean
    activeWhenPathIncludes?: string
    activeWhenPathExcludes?: string
}

/** Route-prefix → sidebar layer. Longest-match wins (most specific route takes priority). */
export const ROUTE_LAYER_MAP: Record<string, 'workspace' | 'information' | 'strategy' | 'construct'> = {
    '/dashboard': 'workspace',
    '/dashboard/home': 'workspace',
    '/dashboard/brand-strategy/foundations': 'strategy',
    '/dashboard/brand-strategy/construction': 'strategy',
    '/dashboard/brand-strategy/construction/portraits': 'strategy',
    '/dashboard/portraits': 'strategy',
    '/dashboard/portraits/people': 'strategy',
    '/dashboard/portraits/companies': 'strategy',
    '/dashboard/portraits/products': 'strategy',
    '/dashboard/persuasion-lab/stories': 'strategy',
    '/dashboard/persuasion-lab/argument-builder': 'strategy',
    '/dashboard/persuasion-lab/creative-context': 'strategy',
    '/dashboard/persuasion-lab/research': 'strategy',
    '/dashboard/persuasion-lab': 'strategy',
    '/dashboard/visual-engine': 'construct',
    '/dashboard/assets/goals': 'strategy',
    '/dashboard/assets/targets': 'strategy',
    '/dashboard/assets/tfds': 'strategy',
    '/dashboard/assets/visual-profiles': 'strategy',
    '/dashboard/assets/evidence': 'strategy',
    '/dashboard/assets/heft': 'strategy',
    '/dashboard/assets/companies': 'information',
    '/dashboard/assets/products': 'information',
    '/dashboard/assets/people': 'information',
    '/dashboard/assets/brand-assets': 'information',
    '/dashboard/persona-builder': 'information',
    '/dashboard/documents': 'workspace',
    '/dashboard/document': 'workspace',
    '/dashboard/datasets': 'workspace',
    '/dashboard/templates': 'workspace',
    '/dashboard/media': 'workspace',
    '/dashboard/seo': 'workspace',
    '/dashboard/2hq': 'workspace',
    '/dashboard/agent': 'workspace',
    '/dashboard/flows': 'workspace',
    '/dashboard/content-sheets': 'workspace',
    '/dashboard/prompts': 'workspace',
    '/dashboard/campaigns': 'construct',
    '/dashboard/messaging-campaign': 'construct',
    '/dashboard/tools': 'information',
}

export const SIDEBAR_GROUPS: { key: 'workspace' | 'information' | 'strategy' | 'construct'; label: string }[] = [
    { key: 'workspace', label: 'Workspace' },
    { key: 'information', label: 'Information' },
    { key: 'strategy', label: 'Strategy' },
    { key: 'construct', label: 'Construct' },
]

export function subItemMatchesPath(subItem: SidebarSubMenuItemWithMatch, pathname: string): boolean {
    if (subItem.exactMatch) return pathname === subItem.link || pathname === `${subItem.link}/`
    if (subItem.activeWhenPathIncludes && pathname.includes(subItem.activeWhenPathIncludes)) {
        if (subItem.activeWhenPathExcludes && pathname.includes(subItem.activeWhenPathExcludes)) return false
        return true
    }
    return pathname.startsWith(subItem.link)
}

/** Influencer campaign flows: highlight Influencer Brief, not Campaigns. */
export function applyInfluencerCampaignHighlights(
    idElement: string,
    pathname: string,
    search: string
): { forceLinkActive?: boolean; suppressRouteActive?: boolean } | undefined {
    const onCampaignDetail = /^\/dashboard\/campaigns\/[^/]+\/?$/.test(pathname)
    const onInfluencerWizard = pathname.startsWith('/dashboard/influencer-brief')
    const onCampaignsListInfluencer = pathname === '/dashboard/campaigns' && search.includes('type=influencer')
    const functionalType = localStorage.getItem('cz_current_campaign_functional_type')

    const inInfluencerContext =
        (onCampaignDetail && functionalType === 'influencer') || onInfluencerWizard || onCampaignsListInfluencer

    if (!inInfluencerContext) return undefined
    if (idElement === 'sidebar-influencer-brief') return { forceLinkActive: true }
    if (idElement === 'sidebar-campaigns') return { suppressRouteActive: true }
    return undefined
}

export function applySituationsHighlight(idElement: string, pathname: string): boolean {
    return (
        idElement === 'sidebar-situations' &&
        (pathname.includes('/argument-builder/situations') || /\/persuasion-lab\/[^/]+\/stories/.test(pathname))
    )
}
